import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { hasProductAccess, type ProductFeatureMap } from '@tfa/shared'
import { useAuth, AuthProvider } from './hooks/useAuth'
import { useAppAccessBlocked, useClientSuspended } from './hooks/useAppAccess'
import { useOnboardingCompleting } from './lib/onboardingCompleting'
import { TestWeekStories } from './components/TestWeekStories'
import { MainModeStories } from './components/MainModeStories'
import { StartupWindowHost } from './components/StartupWindowHost'
import { useTelegram } from './hooks/useTelegram'
import { useStartParamRouting } from './hooks/useStartParamRouting'
import { useServerEvents } from './hooks/useServerEvents'
import { useBuildVersion } from './hooks/useBuildVersion'
import { useTelemetry } from './hooks/useTelemetry'
import { UpdateBanner } from './components/UpdateBanner'
import { Suspense, useEffect, useRef } from 'react'
import { lazyPage, type LazyPage } from './lib/lazyPage'
import { api, describeApiError } from './services/api'
import { CuratorProvider, useCurator } from './contexts/CuratorContext'
import { MiniPlayerProvider } from './contexts/MiniPlayerContext'
import { Toaster } from 'sonner'
import { SkeletonStyles } from './components/Skeleton'
import { getCharacterImage } from './assets/character'
import { TfaLogo } from './components/TfaLogo'

// Pages. Сразу грузятся только стартовые: Дневник (на него ведёт «/») и
// Главная. Остальные — отдельными чанками при первом переходе и фоновой
// догрузкой после старта (fb-04, см. lib/lazyPage).
import { Home } from './pages/Home'
import { Diary } from './pages/Diary'
const Lectures = lazyPage(() => import('./pages/Lectures'), (m) => m.Lectures)
const LectureView = lazyPage(() => import('./pages/LectureView'), (m) => m.LectureView)
const Leaderboard = lazyPage(() => import('./pages/Leaderboard'), (m) => m.Leaderboard)
const Shop = lazyPage(() => import('./pages/Shop'), (m) => m.Shop)
const Challenges = lazyPage(() => import('./pages/Challenges'), (m) => m.Challenges)
const Chat = lazyPage(() => import('./pages/Chat'), (m) => m.Chat)
const Profile = lazyPage(() => import('./pages/Profile'), (m) => m.Profile)
const Onboarding = lazyPage(() => import('./pages/Onboarding'), (m) => m.Onboarding)
const JoinChats = lazyPage(() => import('./pages/JoinChats'), (m) => m.JoinChats)
const CuratorClients = lazyPage(() => import('./pages/CuratorClients'), (m) => m.CuratorClients)
const CuratorClientProfile = lazyPage(() => import('./pages/CuratorClientProfile'), (m) => m.CuratorClientProfile)
const CuratorProfile = lazyPage(() => import('./pages/CuratorProfile'), (m) => m.CuratorProfile)
const CuratorProgramEditor = lazyPage(() => import('./pages/CuratorProgramEditor'), (m) => m.CuratorProgramEditor)
const WorkoutView = lazyPage(() => import('./pages/WorkoutView'), (m) => m.WorkoutView)
const WorkoutActive = lazyPage(() => import('./pages/WorkoutActive'), (m) => m.WorkoutActive)
const WorkoutSummary = lazyPage(() => import('./pages/WorkoutSummary'), (m) => m.WorkoutSummary)
const WorkoutTemplates = lazyPage(() => import('./pages/WorkoutTemplates'), (m) => m.WorkoutTemplates)
const Settings = lazyPage(() => import('./pages/Settings'), (m) => m.Settings)
const CharacterModal = lazyPage(() => import('./pages/CharacterModal'), (m) => m.CharacterModal)
const NoProductAccess = lazyPage(() => import('./pages/NoProductAccess'), (m) => m.NoProductAccess)
const SuspendedAccess = lazyPage(() => import('./pages/SuspendedAccess'), (m) => m.SuspendedAccess)
const MeasurementsHistory = lazyPage(() => import('./pages/MeasurementsHistory'), (m) => m.MeasurementsHistory)
const ProgressPhotos = lazyPage(() => import('./pages/ProgressPhotos'), (m) => m.ProgressPhotos)
const ProgramPicker = lazyPage(() => import('./pages/ProgramPicker'), (m) => m.ProgramPicker)
const Analytics = lazyPage(() => import('./pages/Analytics'), (m) => m.Analytics)

/** Экраны клиента, которые догружаем в фоне после старта — чтобы переход по вкладкам не ждал сети. */
const CLIENT_PAGES: LazyPage[] = [
  Lectures, LectureView, Leaderboard, Shop, Challenges, Chat, Profile, WorkoutView, WorkoutActive,
  WorkoutSummary, WorkoutTemplates, Settings, CharacterModal, MeasurementsHistory, ProgressPhotos,
  ProgramPicker, Analytics,
]
const CURATOR_PAGES: LazyPage[] = [CuratorClients, CuratorClientProfile, CuratorProfile, CuratorProgramEditor]

/** Фоновая догрузка экранов, когда старт уже отрисован и сеть освободилась. */
function usePreloadPages(pages: LazyPage[]) {
  useEffect(() => {
    const t = window.setTimeout(() => {
      const run = () => pages.forEach((p) => p.preload())
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
      if (ric) ric(run)
      else run()
    }, 3000)
    return () => window.clearTimeout(t)
  }, [pages])
}

/** Заглушка на время догрузки экрана — лёгкая, без персонажа сплэша. */
function PageFallback() {
  return (
    <div data-testid="page-fallback" style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #EEF2FF 0%, #EDF4FF 50%, #F0F4F8 100%)',
    }}>
      <div style={{
        width: 26, height: 26,
        border: '3px solid rgba(108,92,231,0.18)',
        borderTopColor: '#6C5CE7',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  )
}

const F = 'Roboto, sans-serif'

function LoadingScreen() {
  // Бренд-сплэш: финальная форма персонажа (ур. 17) + лого ТФА. Чтобы пользователь
  // сразу видел «целевой образ», а не стартовый стейдж.
  const mascotSrc = getCharacterImage(17)
  return (
    <div data-testid="loading-screen" style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #EEF2FF 0%, #EDF4FF 50%, #F0F4F8 100%)',
      fontFamily: F, padding: '32px 24px',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22 }}>
        {/* TFA logo + wordmark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TfaLogo size={42} />
          <span style={{
            fontSize: 26, fontWeight: 900, color: '#1C2D41',
            letterSpacing: 2,
          }}>
            ТФА
          </span>
        </div>

        {/* Mascot — ур. 17 */}
        {mascotSrc && (
          <img
            src={mascotSrc}
            alt=""
            width={220}
            height={220}
            draggable={false}
            style={{
              display: 'block', objectFit: 'contain',
              animation: 'mascotFloat 2.4s ease-in-out infinite',
              filter: 'drop-shadow(0 14px 36px rgba(108,92,231,0.28))',
            }}
          />
        )}

        {/* Spinner */}
        <div style={{
          width: 26, height: 26,
          border: '3px solid rgba(108,92,231,0.18)',
          borderTopColor: '#6C5CE7',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes mascotFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  )
}

/** Снимок identity-флагов localStorage — для диагностики рассинхрона сессии. */
function identityFlagsSnapshot(): Record<string, string> {
  const keys = ['tfa_is_curator', 'tfa_is_also_curator', 'tfa_is_also_client', 'tfa_admin_view', 'tfa_viewing_client_id']
  const out: Record<string, string> = {}
  for (const k of keys) {
    const v = localStorage.getItem(k)
    if (v != null) out[k] = v
  }
  out.hasToken = api.getToken() ? '1' : '0'
  return out
}

/** Полный сброс сессии Mini App (bug #213): чистый релогин при рассинхроне. */
function resetSessionAndReload() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('tfa_'))
      .forEach((k) => localStorage.removeItem(k))
    api.setToken(null)
  } catch { /* ignore */ }
  window.location.reload()
}

function ErrorScreen({ error }: { error: Error }) {
  const { close } = useTelegram()
  const detail = describeApiError(error)

  // Beacon на бэкенд (bug #213): консоль в мобильном Telegram недоступна, поэтому
  // шлём детали ошибки в логи API + админ-бот, чтобы поймать реальную причину.
  const reported = useRef(false)
  useEffect(() => {
    if (reported.current) return
    reported.current = true
    const tg = (window as unknown as { Telegram?: { WebApp?: { platform?: string; version?: string } } }).Telegram?.WebApp
    api.reportClientError({
      context: 'init',
      status: detail.status,
      code: detail.code,
      endpoint: detail.endpoint,
      method: detail.method,
      message: detail.message,
      // build=<sha> в конце строки: без него нельзя понять, на какой сборке
      // сидит человек — именно этого не хватило в разборе #321.
      platform: `${tg?.platform ?? 'web'}/${tg?.version ?? '?'} build=${import.meta.env.VITE_BUILD_ID ?? 'dev'} ${navigator.userAgent}`.slice(0, 200),
      isCurator: localStorage.getItem('tfa_is_curator') === 'true',
      hasToken: !!api.getToken(),
      flags: identityFlagsSnapshot(),
    })
  }, [detail.status, detail.code, detail.endpoint, detail.method, detail.message])

  // Сетевой сбой (ответа не было) — не рассинхрон сессии: чистить токены и
  // заставлять логиниться заново бессмысленно, помогает повтор со связью.
  const offline = detail.isNetwork

  // Техническая строка для отчёта: «403 ACCESS_CLOSED · POST /auth/telegram».
  const techParts = [
    detail.status != null ? String(detail.status) : null,
    detail.code || null,
    detail.endpoint ? `${detail.method ?? ''} ${detail.endpoint}`.trim() : null,
  ].filter(Boolean)

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #EEF2FF 0%, #EDF4FF 50%, #F0F4F8 100%)',
      fontFamily: F, padding: 24,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center', maxWidth: 320 }}>
        <span style={{ fontSize: 48 }}>{offline ? '📡' : '😔'}</span>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1C2D41', margin: 0, letterSpacing: '-0.2px' }}>
          {offline ? 'Нет связи с сервером' : 'Что-то пошло не так'}
        </h2>
        <p style={{ fontSize: 15, color: '#6B7B8D', margin: 0, lineHeight: 1.5 }}>
          {offline ? 'Проверьте интернет и попробуйте снова — данные никуда не пропали.' : detail.message}
        </p>
        {techParts.length > 0 && (
          <code
            data-testid="error-tech"
            style={{
              fontSize: 12, color: '#94A3B8', background: 'rgba(26,58,92,0.05)',
              borderRadius: 8, padding: '6px 10px', wordBreak: 'break-all', lineHeight: 1.4,
            }}
          >
            {techParts.join(' · ')}
          </code>
        )}
        {/* #253: «Попробовать снова» (reload) не чинит протухшую сессию — частая
            причина 403 — и убрана. «Войти заново» = чистый релогин, реально помогает.
            Исключение — сетевой сбой: там сессия цела, нужен именно повтор. */}
        <button
          data-testid={offline ? 'btn-retry' : 'btn-reset-session'}
          onClick={offline ? () => window.location.reload() : resetSessionAndReload}
          style={{
            width: '100%', height: 54, borderRadius: 18, border: 'none',
            background: 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)',
            color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 8,
            boxShadow: '0 6px 24px rgba(108,92,231,0.35)',
            fontFamily: F, letterSpacing: '0.01em',
          }}
        >
          {offline ? 'Повторить' : 'Войти заново'}
        </button>
        <button
          onClick={close}
          style={{
            background: 'transparent', border: 'none',
            color: '#6B7B8D', fontSize: 15, fontWeight: 600, cursor: 'pointer',
            fontFamily: F,
          }}
        >
          Закрыть
        </button>
      </div>
    </div>
  )
}

function AuthenticatedApp() {
  const { user, needsOnboarding, isCurator } = useAuth()
  const { viewingClientId } = useCurator()
  const accessBlocked = useAppAccessBlocked()
  const suspended = useClientSuspended()
  const navigate = useNavigate()
  const wasBlocked = useRef(false)

  // Доступ появился (SSE / кнопка / опрос) — уводим с того пути, где клиент
  // застал блокер: он мог прийти из онбординга на /lectures, а мог висеть на
  // холодном старте. replace, чтобы кнопка «назад» не вернула на закрытый экран.
  useEffect(() => {
    if (accessBlocked) {
      wasBlocked.current = true
      return
    }
    if (!wasBlocked.current) return
    wasBlocked.current = false
    const uu = user as { product?: string | null; access?: Partial<ProductFeatureMap> | null } | undefined
    navigate(hasProductAccess(uu?.product ?? null, 'lectures', uu?.access) ? '/lectures' : '/diary', { replace: true })
  }, [accessBlocked, user, navigate])
  // Пока показываем экран-поздравление онбординга — держим Onboarding смонтированным,
  // даже если onboardingCompleted уже true (иначе поздравление мгновенно закрывается
  // и сразу видно «Подбираем куратора», баг #178).
  const onboardingCompleting = useOnboardingCompleting()

  // Гейт вступления в чаты: только тарифы без анкеты и только пока отметки нет.
  const gu = user as { product?: string | null; chatsJoinedAt?: string | null } | undefined
  const needsChatsGate = (gu?.product === 'PROGRAM' || gu?.product === 'TRAINING') && !gu?.chatsJoinedAt

  // Продуктовая телеметрия: считаем только настоящие визиты клиентов. Куратор
  // и просмотр клиента его глазами исключены — иначе сотрудники попадут в DAU
  // и в «время в приложении» (API отбрасывает такие пачки, но незачем и слать).
  useTelemetry(() => api.getToken(), !isCurator && !viewingClientId)
  usePreloadPages(isCurator && !viewingClientId ? CURATOR_PAGES : CLIENT_PAGES)

  // Curator mode: route between client list, client profile and curator profile
  if (isCurator && !viewingClientId) {
    return (
      <Routes>
        <Route path="/curator/clients/:id/program/new" element={<CuratorProgramEditor />} />
        <Route path="/curator/clients/:id" element={<CuratorClientProfile />} />
        <Route path="/curator/profile" element={<CuratorProfile />} />
        <Route path="*" element={<CuratorClients />} />
      </Routes>
    )
  }

  if ((needsOnboarding || onboardingCompleting) && !isCurator && !viewingClientId) {
    return <Onboarding />
  }

  // Тариф не подтверждён (product=NONE) — вместо приложения блокирующий экран.
  // Проверка строго ПОСЛЕ онбординга: пока анкета не сдана, показывается он, а
  // порядок веток гарантирует переход без промежуточного кадра с Layout.
  // Приостановка — раньше проверки тарифа: пауза поставлена админом осознанно,
  // и человеку нужно объяснение именно про неё, а не «заверши регистрацию».
  if (suspended) {
    return <SuspendedAccess />
  }

  if (accessBlocked) {
    return <NoProductAccess />
  }

  // #579: Программа и Тренинг анкету не проходят, поэтому шаг «Вступи в чаты»
  // им показывается отдельным обязательным экраном — один раз, по серверной
  // отметке. Строго ПОСЛЕ проверки тарифа: тому, у кого доступа ещё нет, нужен
  // экран про тариф, а не приглашение в чаты купленной программы. Куратор и
  // просмотр клиента его глазами исключены — гейт личный, чужой не закрывают.
  if (needsChatsGate && !isCurator && !viewingClientId) {
    return <JoinChats />
  }

  return (
    <Routes>
      {/* Дневник — стартовый экран при открытии приложения */}
      <Route path="/" element={<Navigate to="/diary" replace />} />
      <Route path="/home" element={<Home />} />
      <Route path="/lectures" element={<Lectures />} />
      <Route path="/lectures/:id" element={<LectureView />} />
      <Route path="/content" element={<Lectures />} />
      <Route path="/content/lecture/:id" element={<LectureView />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/leaderboard/challenges" element={<Challenges />} />
      <Route path="/leaderboard/survival" element={<Challenges />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/challenges" element={<Challenges />} />
      <Route path="/diary" element={<Diary />} />
      <Route path="/diary/workout" element={<WorkoutView />} />
      <Route path="/diary/workout/active" element={<WorkoutActive />} />
      <Route path="/diary/workout/summary" element={<WorkoutSummary />} />
      <Route path="/workout" element={<WorkoutView />} />
      <Route path="/workout/templates" element={<WorkoutTemplates />} />
      <Route path="/measurements" element={<MeasurementsHistory />} />
      <Route path="/progress-photos" element={<ProgressPhotos />} />
      {/* #538: выбор готовой программы — отдельный экран, доступный в любой момент. */}
      <Route path="/workout/programs" element={<ProgramPicker />} />
      <Route path="/analytics" element={<Analytics />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/character" element={<CharacterModal />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="*" element={<Navigate to="/diary" replace />} />
    </Routes>
  )
}

function AppContent() {
  const { isAuthenticated, isLoading, error } = useAuth()
  const { isAdminView } = useCurator()
  const { ready } = useTelegram()
  const accessBlocked = useAppAccessBlocked()
  useStartParamRouting()
  useServerEvents(isAuthenticated)

  useEffect(() => {
    if (!isLoading) {
      ready()
    }
  }, [isLoading, ready])

  // Вариант A (как Duolingo): «день» (серия, дневной кап XP, календарь активности)
  // считается по таймзоне устройства. Отдаём её на бэк при загрузке, если
  // изменилась с прошлого раза — чтобы серверная логика совпадала с тем, что
  // клиент видит в календаре. Не клиент (куратор/админ) — бэк сам игнорит.
  // Админ «за пользователя» не шлёт вовсе: его токен read-only, сервер отвечает
  // 403 на любой не-GET, и в консоли висела ошибка на каждом входе.
  useEffect(() => {
    if (!isAuthenticated || isAdminView) return
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (!tz || localStorage.getItem('tfa_device_tz') === tz) return
      api.setTimezone(tz)
        .then(() => localStorage.setItem('tfa_device_tz', tz))
        .catch(() => { /* не критично — попробуем при следующей загрузке */ })
    } catch { /* Intl недоступен — игнорируем */ }
  }, [isAuthenticated, isAdminView])

  // Сборка Mini App → на клиента в БД, чтобы при разборе жалобы было видно, на
  // каком бандле человек (мобильный Telegram не даёт консоль, а логи Fastify на
  // стендах выключены). Шлём ТОЛЬКО при смене — маркер в localStorage, ровно как
  // с таймзоной выше: одна запись в БД на юзера на версию, а не на загрузку.
  // Админу «за пользователя» не шлём — по той же причине, что и таймзону.
  useEffect(() => {
    if (!isAuthenticated || isAdminView) return
    const build = import.meta.env.VITE_BUILD_ID
    if (!build) return
    try {
      if (localStorage.getItem('tfa_app_build') === build) return
      api.setAppBuild(build)
        .then(() => localStorage.setItem('tfa_app_build', build))
        .catch(() => { /* не критично — попробуем при следующей загрузке */ })
    } catch { /* localStorage недоступен — игнорируем */ }
  }, [isAuthenticated, isAdminView])

  // Фолбэк: гарантированно зовём ready() через таймаут, даже если авторизация
  // «висит» (isLoading не сходится). Иначе Telegram держит свой плейсхолдер и
  // у клиента вечный белый экран. ready() лишь разрешает показ WebView —
  // в худшем случае пользователь увидит экран загрузки/ошибки, а не белизну.
  // Через ref + пустые deps: таймер не сбрасывается ре-рендерами (важно, если
  // авторизация крутится в цикле — иначе таймаут никогда бы не выстрелил).
  const readyRef = useRef(ready)
  readyRef.current = ready
  useEffect(() => {
    const t = setTimeout(() => readyRef.current(), 2000)
    return () => clearTimeout(t)
  }, [])

  if (isLoading) {
    return <LoadingScreen />
  }

  // Ошибка показывается только пока пользователя нет. При живой сессии она
  // могла прийти от уже неактуального запроса (повисший GET /auth/me после
  // успешного релогина) — выкидывать из работающего приложения из-за неё нельзя.
  if (error && !isAuthenticated) {
    return <ErrorScreen error={error} />
  }

  if (!isAuthenticated) {
    return <ErrorScreen error={new Error('Не удалось авторизоваться')} />
  }

  return (
    <>
      <Suspense fallback={<PageFallback />}>
        <AuthenticatedApp />
      </Suspense>
      {/* Сторис само-гейтятся по mode, но живут отдельной веткой дерева с
          zIndex 2000 — при аномалии (product=NONE + mode=ACTIVE, достижимо
          админской правкой) они перекрыли бы блокирующий экран. */}
      {!accessBlocked && (
        <>
          {/* Онбординг-сторис тестовой недели (само-гейтится: TEST_WEEK, 1 раз) */}
          <TestWeekStories />
          {/* Сторис основного режима (само-гейтится: ACTIVE, 1 раз) */}
          <MainModeStories />
          {/* Стартовые окна из админки: ждут закрытия сторис (overlayGate) и
              своих гейтов; zIndex 1900 — ниже сторис */}
          <StartupWindowHost />
        </>
      )}
    </>
  )
}

/**
 * Автообновление сборки. Живёт ВНЕ AppContent намеренно: проверка версии должна
 * работать и когда авторизация висит или упала — старый бандл сам может быть
 * причиной поломки, и тогда обновление её и вылечит.
 */
function UpdateGate() {
  const { updateReady, applyUpdate } = useBuildVersion()
  if (!updateReady) return null
  return <UpdateBanner onApply={applyUpdate} />
}

export function App() {
  return (
    <BrowserRouter>
      <CuratorProvider>
        {/* Одна сессия на всё дерево: useAuth() зовут ~27 компонентов, и без
            провайдера каждый заводил собственный авто-логин (пачки по 5-6
            POST /auth/telegram на один старт). AuthProvider внутри
            CuratorProvider — состояние авторизации читает роль оттуда. */}
        <AuthProvider>
          <SkeletonStyles />
          <UpdateGate />
          {/* #513: кадр лекции живёт здесь, вне Routes — иначе уход со страницы
              размонтировал бы iframe и видео начиналось бы заново. */}
          <MiniPlayerProvider>
            <AppContent />
          </MiniPlayerProvider>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                fontFamily: 'Roboto, sans-serif',
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 600,
              },
            }}
          />
        </AuthProvider>
      </CuratorProvider>
    </BrowserRouter>
  )
}
